import TenantService from '@/services/TenantService';
import UserService from '@/services/UserService';
import CashRegisterService from '@/services/CashRegisterService';
import ApiService from '@/services/ApiService';
import FiscalService from '@/services/FiscalService';
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import LocalNetworkService from '@/services/LocalNetworkService';

import User from '@/models/User';
import Role from '@/models/Role';
import Product from '@/models/Product';
import TaxRate from '@/models/TaxRate';
import ProductCategory from '@/models/ProductCategory';
import Order from '@/models/Order';
import OrderItemComment from '@/models/OrderItemComment';
import Client from '@/models/Client';
import Sector from '@/models/Sector';
import Table from '@/models/Table';
import Grid from '@/models/Grid';
import Button from '@/models/Button';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import CashRegisterTransaction from '@/models/CashRegisterTransaction';
import UserTransaction from '@/models/UserTransaction';
import ProductIngredient from '@/models/ProductIngredient';
import KitchenTicket from '@/models/KitchenTicket';
import Terminal from '@/models/Terminal';
import Session from '@/models/Session';
import Shortcut from '@/models/Shortcut';
import Pricelist from '@/models/Pricelist';

import Orm from '@/store/plugins/orm/Orm';
import Device from '@/wrappers/Device';
import Locale from '@/wrappers/Locale';
import ErrorReporter from '@/wrappers/ErrorReporter';
import * as types from '@/store/mutationTypes';

const OFFLINE_FROM_KEY = 'offline-from';

export default {
    /**
     * Initialize application
     */
    async initializeApp({ commit, dispatch, getters }, { tntoken, token }) {

        // Initialize the store from local storage first and then from server
        await dispatch('initializeStore');

        // Detect device and set it
        const device = await Device.detect()
        Device.set(device)

        if(device.platform === 'android' || device.platform === 'ios') {
            const locale = await Locale.getLocale()
            if(!locale) return 'selectLocale'
        }

        // Detect default locale and language
        await Locale.setLocale()
        Locale.setLanguage()

        // Initialize services
        LocalNetworkService.init()

        // Set tenant
        TenantService.setTenant(getters.tenant, false);

        // Set user
        UserService.setUser(getters.user);

        // Check connection (defaults to true)
        const isOnline = await dispatch('connectionCheck');

        // Initiate Fiscal Service
        FiscalService.init()
        FiscalServiceLegacy.init()

        if(!isOnline) {
            TenantService.startTenantServices()
            commit(types.SET_INITIALIZED, { initialized: true });
            EventBus.$emit('app-initialized')
            return
        }

        const params = {}
        if(tntoken) {
            params.tntoken = tntoken
            TenantService.setToken(tntoken)
        }

        if(token) {
            params.token = token
            UserService.setToken(token)
        }

        // get the initial data from server and set the store
        const response = await ApiService.get('/initialize', { params: params })

        // set tenant
        TenantService.setTenant(response.data.tenant);

        // set user
        UserService.setUser(response.data.user);

        // set cash register
        if(!CashRegisterService.getId() && response.data.cashRegister) {
            CashRegisterService.setCashRegister(response.data.cashRegister);
        }

        // load store data
        if(response.data.appConfig) {
            commit(types.SET_APP_CONFIG, { appConfig: response.data.appConfig });
        }

        try {
            // load data
            if(response.data.tenant) {
                await TenantService.loadData(true)
            }
        } catch(error) {
            console.log(error.message)
        } finally {
            commit(types.SET_INITIALIZED, { initialized: true });
            EventBus.$emit('app-initialized')
        }
    },

    /**
     * Load initial store state from the local storage
     */
    async initializeStore({ commit }) {
        const namespace = Orm.namespace

        const data = await Promise.all([
            AppStorage.store().getItem(`${namespace}.users`),
            AppStorage.store().getItem(`${namespace}.roles`),
            AppStorage.store().getItem(`${namespace}.sessions`),
            AppStorage.store().getItem(`${namespace}.products`),
            AppStorage.store().getItem(`${namespace}.product_categories`),
            AppStorage.store().getItem(`${namespace}.product_ingredients`),
            AppStorage.store().getItem(`${namespace}.tax_rates`),
            AppStorage.store().getItem(`${namespace}.orders`),
            AppStorage.store().getItem(`${namespace}.order_items`),
            AppStorage.store().getItem(`${namespace}.order_item_comments`),
            AppStorage.store().getItem(`${namespace}.clients`),
            AppStorage.store().getItem(`${namespace}.sectors`),
            AppStorage.store().getItem(`${namespace}.tables`),
            AppStorage.store().getItem(`${namespace}.grids`),
            AppStorage.store().getItem(`${namespace}.buttons`),
            AppStorage.store().getItem(`${namespace}.shifts`),
            AppStorage.store().getItem(`${namespace}.cash_registers`),
            AppStorage.store().getItem(`${namespace}.cash_register_transactions`),
            AppStorage.store().getItem(`${namespace}.shortcuts`),
            AppStorage.store().getItem(`${namespace}.user_transactions`),
            AppStorage.store().getItem(`${namespace}.pricelists`),
            AppStorage.store().getItem(`${namespace}.notifications`),
            AppStorage.store().getItem(`${namespace}.kitchen_tickets`),
            AppStorage.store().getItem(`${namespace}.terminals`),
            AppStorage.store().getItem('lastTenant', null),
            AppStorage.store().getItem('tenant'),
            AppStorage.store().getItem('user'),
            AppStorage.store().getItem('curOrder'),
            AppStorage.store().getItem('curOrderItem'),
            AppStorage.store().getItem('curUnpaidOrders'),
            AppStorage.store().getItem('curTable'),
            AppStorage.store().getItem('appConfig'),
            AppStorage.store().getItem('printConfig'),
            AppStorage.store().getItem('device'),
            AppStorage.store().getItem('workingDays'),
            AppStorage.store().getItem('updatePending'),
            AppStorage.store().getItem('tablePermissions'),
            AppStorage.store().getItem('pausedOrder'),
            AppStorage.store().getItem('activePricelist'),
        ])

        const isValidJSON = str => {
            try {
                if(!str) return false
                const json = JSON.parse(str);
                return !!json;
            } catch (e) {
                return false;
            }
        }

        const parseJSON = (data, fallback) => isValidJSON(data) ? JSON.parse(data) : fallback

        if(data[0]) User.init({ data: parseJSON(data[0], []) });
        if(data[1]) Role.init({ data: parseJSON(data[1], []) });
        if(data[2]) Session.init({ data: parseJSON(data[2], []) });
        if(data[3]) Product.init({ data: parseJSON(data[3], []), freeze: true });
        if(data[4]) ProductCategory.init({ data: parseJSON(data[4], []) });
        if(data[5]) ProductIngredient.init({ data: parseJSON(data[5], []) });
        if(data[6]) TaxRate.init({ data: parseJSON(data[6], []) });
        if(data[7]) Order.init({ data: parseJSON(data[7], []), freeze: true });
        if(data[8]) OrderItem.init({ data: parseJSON(data[8], []) });
        if(data[9]) OrderItemComment.init({ data: parseJSON(data[9], []) });
        if(data[10]) Client.init({ data: parseJSON(data[10], []) });
        if(data[11]) Sector.init({ data: parseJSON(data[11], []) });
        if(data[12]) Table.init({ data: parseJSON(data[12], []) });
        if(data[13]) Grid.init({ data: parseJSON(data[13], []) });
        if(data[14]) Button.init({ data: parseJSON(data[14], []) });
        if(data[15]) Shift.init({ data: parseJSON(data[15], []) });
        if(data[16]) CashRegister.init({ data: parseJSON(data[16], []) });
        if(data[17]) CashRegisterTransaction.init({ data: parseJSON(data[17], []) });
        if(data[18]) Shortcut.init({ data: parseJSON(data[18], []) });
        if(data[19]) UserTransaction.init({ data: parseJSON(data[19], []) });
        if(data[20]) Pricelist.init({ data: parseJSON(data[20], []) });
        if(data[21]) Notification.init({ data: parseJSON(data[21], []) });
        if(data[22]) KitchenTicket.init({ data: parseJSON(data[22], []) });
        if(data[23]) Terminal.init({ data: parseJSON(data[23], []) });

        const curOrder = parseJSON(data[27], null)
        const curOrderItem = parseJSON(data[28], null)
        const curUnpaidOrders = parseJSON(data[29], [])
        const curTable = parseJSON(data[30], null)
        const pausedOrder = parseJSON(data[37], null)

        const state = {
            lastTenant: parseJSON(data[24], null),
            tenant: parseJSON(data[25], null),
            user: parseJSON(data[26], null),
            curOrder: curOrder ? new Order(curOrder) : null,
            curOrderItem: curOrderItem ? new OrderItem(curOrderItem) : null,
            curUnpaidOrders: curUnpaidOrders ? curUnpaidOrders.map(order => new Order(order)) : [],
            curTable: curTable ? new Table(curTable) : null,
            appConfig: parseJSON(data[31], null),
            printConfig: parseJSON(data[32], null),
            device: parseJSON(data[33], null),
            workingDays: parseJSON(data[34], []),
            updatePending: parseJSON(data[35], false),
            tablePermissions: parseJSON(data[36], {}),
            pausedOrder: pausedOrder ? new Order(pausedOrder) : null,
            activePricelist: parseJSON(data[36], 0),
        }

        commit(types.INIT_STORE, state);
    },

    /**
     * Perform connection check
     */
    async connectionCheck({ commit, dispatch, getters }) {
        try {
            await dispatch('checkOnline')
            await dispatch('sync')

            AppStorage.local().deleteItem(OFFLINE_FROM_KEY);

            commit(types.SET_IS_ONLINE, { isOnline: true });
            return true
        } catch (error) {
            const offlineFrom = AppStorage.local().getItem(OFFLINE_FROM_KEY);
            const offlineLimitMinutes = locale === 'me' ? (2 * 24 * 60) : (5 * 24 * 60)
            if(!offlineFrom) {
                AppStorage.local().setItem(OFFLINE_FROM_KEY, moment().format('YYYY-MM-DD HH:mm:ss'));
            } else if(moment().diff(moment(offlineFrom), 'minutes') > offlineLimitMinutes) {
                EventBus.$emit('show-offline-too-long');
            }

            commit(types.SET_IS_ONLINE, { isOnline: false });
            return false
        }
    },

    /**
     * Send a an empty request to the server to check the connection
     */
    async checkOnline({ commit }) {
        try {
            const response = await ApiService.get('/check')
            if (!response || !response.data || !response.data.success) {
                throw Error('Client is offline');
            }
        } catch (error) {
            throw Error('Client is offline. ' + error.message);
        }
    },

    /**
     * Perform offline data sync
     */
    async sync({ commit, dispatch, getters }, force = false) {
        const tenant = getters.tenant;
        const venue = getters.tenant ? getters.tenant.venue : null;
        const syncInProgress = getters.syncInProgress;
        const timeout = getters.syncOptions?.timeout || 120000;
        const limit = getters.syncOptions?.limit || 50;
        const autoSyncDisabled = !force && !!getters.syncOptions?.autoSyncDisabled;

        if(!tenant || !venue || syncInProgress || autoSyncDisabled) {
            return {
                success: false,
                details: `Sync denied. Reason:${!tenant ? ' tenant not set' : ''}` +
                    `${!venue ? ' venue not set' : ''}` +
                    `${syncInProgress ? ' syncInProgress set' : ''}` +
                    `${autoSyncDisabled ? ' autoSyncDisabled set' : ''}`
            }
        }

        commit(types.SET_SYNC_IN_PROGRESS, { syncInProgress: true });

        let shifts = Shift.all().filter(shift => !!shift.sync_needed);
        let userTransactions = UserTransaction.all().filter(transaction => !!transaction.sync_needed)
        let orders = Order.all().filter(order => !!order.sync_needed && order.items.length > 0);
        let sessions = Session.all().filter(session => !!session.sync_needed)
        let cashRegisterTransactions = CashRegisterTransaction.all().filter(transaction => !!transaction.sync_needed)
        let kitchenTickets = KitchenTicket.all().filter(kt => !!kt.sync_needed)

        // if there's nothing to sync
        if (!shifts.length && !orders.length && !userTransactions.length && !sessions.length && !cashRegisterTransactions.length && !kitchenTickets.length) {
            commit(types.SET_SYNC_IN_PROGRESS, { syncInProgress: false });
            return {
                success: false,
                details: 'No data to sync',
            }
        }

        EventBus.$emit('sync-status', 'inProgress');

        try {
            const orderNumToSync = orders.length;

            // Sync with server
            let syncResult = await dispatch('syncWithServer', {
                shifts,
                orders: orders.splice(0, limit),
                userTransactions,
                sessions,
                cashRegisterTransactions,
                kitchenTickets,
                options: { timeout }
            });

            let syncedOrders = syncResult.orders;

            while(orders.length > 0) {
                syncResult = await dispatch('syncWithServer', {
                    orders: orders.splice(0, limit),
                    options: { timeout }
                });

                syncedOrders = [...syncedOrders, syncResult.orders]
            }

            EventBus.$emit('sync-status', null);

            // Remove all items that synced from the native store
            await dispatch('deleteSyncedFromNativeStore', { syncedOrders })

            // Remove all items that are still left unsynced in the store
            await dispatch('deleteUnsynced')

            return {
                success: true,
                details: `Synced ${orderNumToSync} orders, ${userTransactions.length} userTransactions, ${cashRegisterTransactions.length} cashRegisterTransactions, ${sessions.length} sessions, ${kitchenTickets.length} kitchenTickets`,
            }
        } catch (error) {
            EventBus.$emit('sync-status', 'failed');

            console.log(error.message)
            ErrorReporter.reportMessage('Sync failed', { error: error.message })

            throw {
                message: 'sync_exception',
                details: error.message
            };
        } finally {
            commit(types.SET_SYNC_IN_PROGRESS, { syncInProgress: false });
        }
    },

    /**
     * Sync with server
     */
    async syncWithServer({ commit, dispatch, getters }, { shifts = [], orders = [], userTransactions = [], sessions = [], cashRegisterTransactions = [], kitchenTickets = [], options = {} }) {
        const timeout = options.timeout || 120000;

        const response = await ApiService.post(
            '/sync',
            {
                shifts: shifts,
                orders: orders,
                user_transactions: userTransactions,
                sessions: sessions,
                cash_register_transactions: cashRegisterTransactions,
                kitchen_tickets: kitchenTickets
            },
            { timeout: timeout }
        )

        Shift.insert({ data: response.data.shifts });
        UserTransaction.insert({ data: response.data.user_transactions });
        Product.insert({ data: response.data.products, freeze: true });
        Session.insert({ data: response.data.sessions });
        Order.insert({ data: response.data.orders, freeze: true });
        CashRegisterTransaction.insert({ data: response.data.cash_register_transactions });
        KitchenTicket.insert({ data: response.data.kitchen_tickets });

        return response.data;
    },

    /**
     * Delete unsynced data from store
     */
    async deleteUnsynced({ commit, dispatch, getters }) {
        let unsyncedOrdersExist = Order.all().some(item => !!item.sync_needed)
        let unsyncedTransactionsExist = CashRegisterTransaction.all().some(item => !!item.sync_needed)
        let unsyncedUsersExist = User.all().some(item => !!item.sync_needed)
        let unsyncedSessionsExist = Session.all().some(item => !!item.sync_needed)
        let unsyncedKitchenTicketsExist = KitchenTicket.all().some(item => !!item.sync_needed)

        if(unsyncedOrdersExist) {
            Order.create({ data: Order.all().filter(item => !item.sync_needed).map(item => item.$toJson()), freeze: true })
        }

        if(unsyncedTransactionsExist) {
            CashRegisterTransaction.create({ data: CashRegisterTransaction.all().filter(item => !item.sync_needed).map(item => item.$toJson()) })
        }

        if(unsyncedUsersExist) {
            User.create({ data: User.all().filter(item => !item.sync_needed).map(item => item.$toJson()) })
        }

        if(unsyncedSessionsExist) {
            Session.create({ data: Session.all().filter(item => !item.sync_needed).map(item => item.$toJson()) })
        }

        if(unsyncedKitchenTicketsExist) {
            KitchenTicket.create({ data: KitchenTicket.all().filter(item => !item.sync_needed).map(item => item.$toJson()) })
        }
    },

    /**
     * Delete synced orders from the native store
     */
    async deleteSyncedFromNativeStore({ commit, dispatch, getters }, { syncedOrders = [] }) {
        if(!syncedOrders.length) {
            syncedOrders = Order.all().filter(o => !o.sync_needed)
        }

        const ordersJson = await AppStorage.native().getItem('orders');
        let orders = ordersJson ? JSON.parse(ordersJson) : [];

        orders = orders.filter(order => {
            const syncedOrder = syncedOrders.find(o => o.hash === order.hash);
            return !syncedOrder || syncedOrder.sync_needed;
        })

        AppStorage.native().setItem('orders', JSON.stringify(orders));
    },

    /**
     * Delete synced orders from the native store
     */
    async syncFromNativeStore({ commit, dispatch, getters }, timeout = 120000) {
        const ordersJson = await AppStorage.native().getItem('orders');
        let orders = ordersJson ? JSON.parse(ordersJson) : [];

        if(!orders.length) {
            return {
                success: false,
                details: 'No data to sync',
                result: null
            }
        }

        let syncResult = await dispatch('syncWithServer', {
            orders,
            shifts: [],
            userTransactions: [],
            sessions: [],
            cashRegisterTransactions: [],
            kitchenTickets: [],
            options: { timeout }
        });

        // Remove all items that synced from the native store
        await dispatch('deleteSyncedFromNativeStore', { syncedOrders: syncResult.orders })

        return {
            success: true,
            details: `Synced ${syncResult.orders.length} orders`,
            result: syncResult
        }
    },
};
